import {gql} from '@apollo/client';
import {
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
} from '@igp/shared/api';
import type {QueryExportPlayerEcologyWeeklyReportsArgs, QueryGetPlayerEcologyWeeklyReportsArgs} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';
import type {DocumentNode} from 'graphql';

import type {PlayerEcologyWeeklyFilterKeys, PlayerEcologyWeeklyQueryFields, PlayerEcologyWeeklyTextFilterKeys} from './types';

export class PlayerEcologyWeeklyRequestBuilder
    extends GqlRequestBuilder<
        EntityType,
        QueryGetPlayerEcologyWeeklyReportsArgs,
        PlayerEcologyWeeklyQueryFields,
        PlayerEcologyWeeklyFilterKeys
    >
    implements IDownloadByLinkRequestBuilder<QueryExportPlayerEcologyWeeklyReportsArgs, PlayerEcologyWeeklyQueryFields>
{
    protected validateFilters(_filter: Partial<Record<PlayerEcologyWeeklyFilterKeys, unknown>>): void {
        return;
    }
    protected buildQuery(fields: PlayerEcologyWeeklyQueryFields[]): DocumentNode {
        const requiredFileds: PlayerEcologyWeeklyQueryFields[] = ['row_id', 'player_uid', 'player_username'];
        return gql`
            query GetPlayerEcologyWeeklyReports($filter: PlayerEcologyWeeklyReportFilter, $sort: Sorting, $start: Int, $end: Int) {
                getPlayerEcologyWeeklyReports(filter: $filter, sort: $sort, end: $end, start: $start) {
                    items {
                        ${this.buildQueryFields([...new Set([...fields, ...requiredFileds])])}
                    }
                    total_count
                }
            }
        `;
    }
    protected buildFilter(filter: Partial<Record<PlayerEcologyWeeklyFilterKeys, unknown>>): QueryGetPlayerEcologyWeeklyReportsArgs {
        const referrer_player_id = this.toGQLStringFilter(filter, 'referrer_id') ?? this.toGQLStringFilter(filter, 'default_referrer_id');
        const is_downstream: boolean =
            filter.is_downstream !== undefined && filter.is_downstream !== null
                ? this.toGQLBooleanFilter(filter, 'is_downstream')
                : this.toGQLBooleanFilter(filter, 'default_is_downstream');
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: PlayerEcologyWeeklyTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
                referrer: referrer_player_id
                    ? {
                          referrer_player_id,
                          is_downstream,
                          is_agent: false,
                      }
                    : undefined,
                report_week_date: this.toGQLDateRange(
                    this.getLatestTimestamp(filter.report_week_date_from as number, filter.report_week_date_min as number),
                    filter.report_week_date_to
                ),
            },
        };
    }
    buildDownloadRequest(
        payload: GqlDownloadRequestPayload<PlayerEcologyWeeklyQueryFields>
    ): GqlRequest<QueryExportPlayerEcologyWeeklyReportsArgs> {
        const filter = this.mapToFilterObject(payload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: payload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: payload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    private buildExportQuery() {
        return gql`
            query ExportPlayerEcologyWeeklyReports(
                $filter: PlayerEcologyWeeklyReportFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportPlayerEcologyWeeklyReports(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }

    private readonly filterFieldsMapper: Record<PlayerEcologyWeeklyTextFilterKeys, PlayerEcologyWeeklyQueryFields[]> = {
        player_uid_username: ['player_uid', 'player_username'],
        player_uid: ['player_uid'],
        player_username: ['player_username'],
        country: ['country'],
    };
}
